// Review hold: routes that park a non-pass result for a human instead of
// failing it. The backend ends those sessions with status PENDING and
// process_state COMPLETED. Every status handler in the app maps
// "COMPLETED and not PASS" to FAIL, so without this the user is told
// "We couldn't verify you" while their submission is actually waiting
// in the review queue.
//
// Route keys (dcams_routes.settings, surfaced as
// settings.journey.settings by get-session):
//   storageOnly         upload-only route, no automated check
//   manualReviewOnFail  automated check ran, a non-pass is held
//   reviewHoldPublisherStatus  what the parent window receives for a
//                       hold: "FAIL" (default, unchanged behaviour)
//                       "PENDING" (legacy initialize.js onErrorPending)
//                       or "PASS" (upload-only integrations that close
//                       their modal only in onSuccess; the screen still
//                       shows the review variant)

export const REVIEW_STATUS = "REVIEW";

export function isReviewHoldRoute(journeySettings) {
  return (
    journeySettings?.storageOnly === true ||
    journeySettings?.manualReviewOnFail === true
  );
}

// True when a status-check response is the held terminal state on a
// route configured for review.
export function isReviewHoldResult(result, journeySettings) {
  if (!result || !isReviewHoldRoute(journeySettings)) return false;
  return result.process_state === "COMPLETED" && result.status === "PENDING";
}

// Whether a status-check response is terminal (the moment to record
// whether this outcome is a hold or not).
export function isTerminalResult(result) {
  if (!result) return false;
  return (
    result.process_state === "COMPLETED" ||
    result.status === "PASS" ||
    result.status === "FAIL"
  );
}

// Status shown on the result screen. Handlers keep dispatching FAIL
// for a hold (so transitions, passkey release and every other FAIL
// consumer behave as before); the hold flag turns it into REVIEW here.
export function displayStatus(status, reviewHold) {
  return status === "FAIL" && reviewHold ? REVIEW_STATUS : status;
}

// Status sent to the publisher for a terminal outcome. Defaults to the
// existing FAIL so integrations that branch on it (BSS SFCC checkout)
// keep working; a route opts into "PENDING" or "PASS" explicitly.
export function publisherStatus(status, reviewHold, journeySettings) {
  if (status === "FAIL" && reviewHold) {
    const configured = journeySettings?.reviewHoldPublisherStatus;
    return configured === "PENDING" || configured === "PASS"
      ? configured
      : "FAIL";
  }
  return status;
}

export const REVIEW_DEFAULT_TITLE = "We received your submission";
export const REVIEW_DEFAULT_SUBTITLE =
  "Your ID is being reviewed. You will hear from us shortly. You can safely close this window.";

const hasText = (v) => typeof v === "string" && v.trim() !== "";

// Copy for the REVIEW screen. It matches what the route tells the
// publisher for a hold (reviewHoldPublisherStatus), so the screen never
// contradicts the integration: BSS SFCC's pending pair is their generic
// "There are some technical issues." text, while their failure pair is
// the real hold instruction ("This is pending manual review." / "Please
// proceed to checkout.").
//   PASS    -> publisher's success copy
//   PENDING -> publisher's pending copy (resultMessages.error.verificationErrorPending*)
//   FAIL    -> publisher's failure copy (default, also for unknown values)
// Each field falls back to our review defaults when unset or blank.
export function reviewCopy(resultMessages, journeySettings) {
  const route = publisherStatus("FAIL", true, journeySettings);
  const m = resultMessages ?? {};
  let title;
  let subTitle;
  if (route === "PASS") {
    title = m.success?.verificationSuccessTitle;
    subTitle = m.success?.verificationSuccessSubTitle;
  } else if (route === "PENDING") {
    title = m.error?.verificationErrorPendingTitle;
    subTitle = m.error?.verificationErrorPendingSubTitle;
  } else {
    title = m.failure?.verificationFailureTitle;
    subTitle = m.failure?.verificationFailureSubTitle;
  }
  return {
    title: hasText(title) ? title : REVIEW_DEFAULT_TITLE,
    subTitle: hasText(subTitle) ? subTitle : REVIEW_DEFAULT_SUBTITLE,
  };
}
